import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, glyphs } from '../../../Styles'
import { Button, ButtonList, MoveAnimalToPen, PEN_MOVE } from '../..'

import { getSectionById } from '../../../Assets/TestData/sectionsData'
import { ButtonTypes } from '../../../Definitions'

interface SectionMove_Props {
  sectionId: number
  onCancelBtn?: () => void
}
/**
 * SECTION_MOVE
 * @param sectionId Unique identifier for each section.
 * @param onCancelBtn OnClick event to handle Cancel btn.
 * @returns a section of pens for Freeda Location Feeder systems.
 */
export default function SECTION_MOVE({ sectionId, onCancelBtn }: SectionMove_Props) {
  const {t} = useTranslation()
  const section = getSectionById(sectionId);
  const [checkedPens, setCheckedPens] = useState<number[]>([]);
  const [animalsToMove, setAnimalsToMove] = useState<string[]>([]);
  const [showMoveAnimalToPenModal, setMoveAnimalToPenModal] = useState(false) // Move Animal Modal
  const [isAllSelected, setIsAllSelected] = useState(false);
  
  function CancelBtnClick() {
    if (onCancelBtn) onCancelBtn()
  }

  // Error throwback if no section number excists
  if (!section) {
    return <div>Section {sectionId} not found</div>;
  }

  // Handle checking/unchecking single pens
  const handlePenCheck = (penId: number, isChecked: boolean) => {
    setCheckedPens(prevChecked => {
      const updatedPens = isChecked ? [...prevChecked, penId] : prevChecked.filter(p => p !== penId);
      
      const newAnimalIds = section?.pens
          .filter(pen => updatedPens.includes(pen.penId) && pen.animal)
          .map(pen => pen.animal?.animalId?.toString())
          .filter((animalId): animalId is string => animalId !== undefined) || [];

      setAnimalsToMove(newAnimalIds);
      return updatedPens;
    });
  };

  // Handle checking/unchecking all pens
  const handleCheckAllPens = () => {
    if (isAllSelected) {
      setCheckedPens([]);
      setAnimalsToMove([]);
    } else { // if single animal is already checked and "select all" is clicked
      const updatedPens = section?.pens
        .filter(pen => pen.animal)
        .map(pen => pen.penId) || [];
        
      const newAnimalIds = section?.pens
        .filter(pen => pen.animal)
        .map(pen => pen.animal?.animalId?.toString())
        .filter((animalId): animalId is string => animalId !== undefined) || [];
        
      setCheckedPens(updatedPens);
      setAnimalsToMove(newAnimalIds);
    }
    setIsAllSelected(!isAllSelected);
  };

  const MoveModeBtns = 
  <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", width: "100%"}}>
    <Button content={!isAllSelected? t("buttons.selectAll") : t("buttons.deselectAll")} 
      startEnhancer={glyphs.Check({fillColor: colors.systemGreen})} 
      buttonType={ButtonTypes.tableButton} onClick={handleCheckAllPens}
      overrides={{
        baseButton: {backgroundColor: colors.greyLightPrimary},
        hoverStyles: {backgroundColor: colors.greyLightSecondary},
        contentStyles: {color: colors.greyDarkPrimary}
      }}
    /> 
    <ButtonList 
      contents={[`${t("buttons.move")} (${checkedPens.length})`, t("buttons.cancelBtn"), t("buttons.add"), t("buttons.move"), t("buttons.edit")]} 
      onClick={(i => {
        if (i === 0) {setMoveAnimalToPenModal(true)}
        else if (i === 1) {CancelBtnClick()}
      })}
      buttonType={ButtonTypes.tableButton}
      startEnhancers={[
        glyphs.Check({fillColor: colors.greyDarkPrimary}), 
        glyphs.Close({fillColor: colors.greyDarkPrimary}),
        glyphs.Add({fillColor: colors.greyDarkPrimary}), 
        glyphs.AnimalMove({fillColor: colors.greyDarkPrimary}), 
        glyphs.Edit({fillColor: colors.greyDarkPrimary})
      ]}
      disabled={[false, false, true, true, true]}
      overrides={(index) => ({
        baseButton: {
          backgroundColor: index === 3 ? colors.greyLightSecondary : colors.greyLightPrimary
        },
        hoverStyles: {
          backgroundColor: colors.greyLightSecondary
        },
        disabledStyles: {
          opacity: index === 3 ? 1 : 0.5
        },
        contentStyles: {
          color: colors.greyDarkPrimary
        }
      })}
    />
  </div>

  const renderContent = () => {
    return section.pens.map(pen => (
      <PEN_MOVE
        key={pen.animal?.animalId}
        penId={pen.penId}
        onCheck={(isChecked) => handlePenCheck(pen.penId, isChecked)}
        isChecked={checkedPens.includes(pen.penId)}
      />
    ));
  }

  return (
    <>
      <div style={{ width: "fit-content", height: "fit-content" }}>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between", alignItems: "end", padding: "0px 20px" }}>
          {MoveModeBtns}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "", width: "fit-content", gap: "10px", borderRadius: "10px", overflow: "hidden" }}>
          {renderContent()}
        </div>
      </div>
      {/* Move Animal Modal */}
      <MoveAnimalToPen isModalOpen={showMoveAnimalToPenModal} animalsToMove={animalsToMove} goBack={() => setMoveAnimalToPenModal(false)} />
    </>
  );
}